import { cloneElement, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode, Ref } from 'react';

import Tooltip from '@mui/material/Tooltip';

import { EMPTY_STRING } from '@e-socna/empty';

interface Props {
  readonly children: ReactElement<{ ref?: Ref<HTMLElement> }>;
  readonly title: ReactNode;
}

const checkIsTruncated = (element: HTMLElement) =>
  element.scrollWidth > element.clientWidth ||
  element.scrollHeight > element.clientHeight;

export const EllipsisTooltip = ({ children, title }: Readonly<Props>) => {
  const elementRef = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = elementRef.current;

    if (!element) {
      return undefined;
    }

    const updateIsTruncated = () => {
      setIsTruncated(checkIsTruncated(element));
    };

    updateIsTruncated();

    const resizeObserver = new ResizeObserver(updateIsTruncated);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Tooltip title={isTruncated ? title : EMPTY_STRING}>
      {cloneElement(children, { ref: elementRef })}
    </Tooltip>
  );
};
